import React, { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { Col, ListGroup, Row } from "react-bootstrap";
import PropTypes from "prop-types";

import errorToast from "helpers/errorToast";
import usePermissions from "hooks/usePermissions";
import { useGetAttachmentBuckets } from "api/queries/common/useGetAttachmentBuckets";
import ProviderAttachmentFile from "./ProviderAttachmentFile";
import ProviderOtherAttachments from "../ProviderOtherAttachments/ProviderOtherAttachments";
import ProviderOtherAttachmentsCustomBucket from "../ProviderOtherAttachments/ProviderOtherAttachmentsCustomBucket";

const ProviderAttachments = function ({ updateBucketAttachments, handleDraftSave, isQuickRequest }) {
  const intl = useIntl();
  const methods = useFormContext();
  const { setValue, getValues, watch } = methods;
  const { permissions } = usePermissions();
  const {
    company_shipper_settings: { request_attachment_buckets },
  } = JSON.parse(permissions || "{}");

  const location = watch("organizational.location");

  const { data: bucketAttachments = [], refetch: refetchBucketAttachments } = useGetAttachmentBuckets(
    location,
    isQuickRequest,
    {
      enabled: !!location,
      refetchOnMount: false,
      onSuccess: (data) => {
        updateBucketAttachments(data);
        const fetchedBucketAttachmentNames = data.map((result) => result.bucket_name);
        const newBucketAttachmentsFormValue = (getValues("bucket_attachments") || []).filter((bucket) =>
          fetchedBucketAttachmentNames.includes(bucket.bucket_name),
        );
        setValue("bucket_attachments", newBucketAttachmentsFormValue);
      },
      onError: (error) =>
        errorToast(
          error,
          intl.formatMessage({
            id: "new-request.attachments.attachment.couldNotRetrieveAttachmentBucket",
            defaultMessage: "Could not retrieve attachment buckets. Please try again later.",
          }),
        ),
    },
  );

  useEffect(() => {
    refetchBucketAttachments();
  }, [location]);

  const handleAddFile = (file, bucket_name) => {
    let allFiles = (getValues("bucket_attachments") || []).slice();
    const existBefore = allFiles.find((item) => item.bucket_name === bucket_name && item.file === file.file);

    if (existBefore) {
      allFiles = allFiles.map((item) => {
        if (item.bucket_name === bucket_name && item.file === file.file) {
          return {
            ...item,
            id: file.id,
            file: file.file,
            filename: file.filename,
            url: file.url,
            bucket_name,
          };
        }
        return item;
      });
    } else {
      allFiles.push({
        id: file.id,
        file: file.reference,
        filename: file.filename,
        url: file.url,
        bucket_name,
      });
    }
    setValue("bucket_attachments", allFiles);
  };

  const handleDeleteFile = (file) => {
    const allFiles = (getValues("bucket_attachments") || []).slice();
    setValue(
      "bucket_attachments",
      allFiles.filter((f) => f.id !== file.id),
    );
    handleDraftSave?.();
  };

  return (
    <ListGroup variant="flush" className="w-100">
      {request_attachment_buckets && (
        <>
          {bucketAttachments.length > 0 &&
            bucketAttachments.map((bucketAttachment) => (
              <ProviderAttachmentFile
                key={bucketAttachment.id}
                bucket={bucketAttachment}
                onAddFile={handleAddFile}
                onDeleteFile={handleDeleteFile}
              />
            ))}
        </>
      )}
      <Row className="mb-1">
        <Col className="font-weight-bold mb-1">
          <FormattedMessage id="general.otherFiles" defaultMessage="Other files" />
        </Col>
      </Row>
      <Row>
        <Col>
          {request_attachment_buckets && !!bucketAttachments?.find((bucket) => bucket.bucket_name === "Other") ? (
            <ProviderOtherAttachmentsCustomBucket bucketAttachments={bucketAttachments} handleDraftSave={handleDraftSave} />
          ) : (
            <ProviderOtherAttachments handleDraftSave={handleDraftSave} />
          )}
        </Col>
      </Row>
    </ListGroup>
  );
};

ProviderAttachments.propTypes = {
  updateBucketAttachments: PropTypes.func.isRequired,
  handleDraftSave: PropTypes.func,
  isQuickRequest: PropTypes.bool,
};

ProviderAttachments.defaultProps = {
  handleDraftSave: undefined,
  isQuickRequest: false,
};

export default ProviderAttachments;
